import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Receipt from '../validation/Receipt'
import {ReceiptViewGroupedByType, ReceiptViewInterface} from '../../../domain/Receipt/ReceiptView'
import IconFactory from '../svg/IconFactory'
import {ErrorResponse} from '../../../domain/Api/Type'
import ErrorModal from '../modal/ErrorModal';
import { SignedDocument } from '../../../domain/Docusign/SignedDocument';
import Spinner from '../svg/Spinner';
import { KYCControlInterface, KYCDocumentInterface } from '../../../domain/Folder/KYCControl';
import KYCGateway from '../../../gateway/KYC/KYCGateway';
import GetKYCReportUseCase from '../../../useCase/KYC/GetKYCReportUseCase';
import { SubscriberScopeEnum } from '../../../domain/CustomerInfoRecall/SubscriberScopeEnum';
import { IPreviewFile, PreviewFile } from '../modal/PreviewFile';
import PreviewDocumentModal from '../modal/PreviewModal';
import { SubscriberGroupedByScope } from '../../../domain/CustomerInfoRecall/CustomerInfoRecallGatewayInterface';
import MoralPerson from './partials/MoralPerson';
import PhysicalPerson from './partials/PhysicalPerson';

interface Props {
  dossierId: string
  receipts: ReceiptViewGroupedByType
  documents : SignedDocument[]|null;
  kyc: KYCControlInterface|null|undefined
  scope: string
  subscribers: SubscriberGroupedByScope | null
}

const ReceiptsValidationTab: React.FC<Props> = ({dossierId, receipts, documents, kyc, scope, subscribers}) => {
  const {t} = useTranslation()
  const [previewReport, setPreviewReport] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string|null>(null)

  const fetchReportFile = (id: string): Promise<IPreviewFile|null> => {
    const kycUseCase = new GetKYCReportUseCase(new KYCGateway())
    return kycUseCase.handle(dossierId, scope).then((response) => {
      if (response) {
        return new PreviewFile(
          response.resource,
          response.title,
          response.mimeType
        )
      }
      return null
    })
    .catch((errors: ErrorResponse) => {
      if (errors.errors?.length > 0) {
        setErrorMessage(errors.errors[0].message);
      } else {
        setErrorMessage(t(`kyc.unexpected_error`));
      }
      return null
    })
  }

  const findResult = (receiptId: string, receipt: ReceiptViewInterface): KYCDocumentInterface|null => {
    if(!kyc) {
      return null;
    }
    const doc = kyc.documents?.filter((item) => { return item.receiptId === receiptId || item.receiptId === receipt.receiptTypeId})
    if(doc && doc?.length>0) {
      return doc[0];
    }

    return null;
  }

  const getKYCReportCaption = previewReport ? t('button.download-in-progress') : t('button.download-report')
  const getKycDownloadWidget = () => {
    if (!kyc) {
      return (undefined === kyc ) ? <Spinner /> : (<span className="u-txt-italic u-myl">{t('kyc.report_not_available')}</span>)
    }
    if ('error' === kyc.controlStatus) {
      return <span className="u-txt-italic">{t('kyc.error_detected')}</span>
    }
    return (
      <div className="button-preview button-preview--reverse button-preview-download"
        onClick={() => setPreviewReport(true)}
      >
        <IconFactory type="eye"/>
        <span className={`u-prs u-pxs`}>{getKYCReportCaption}</span>
      </div>
    )
  }

  return (
    <>
      { errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} /> }
      {previewReport && <PreviewDocumentModal id={`${dossierId}-kyc-${scope}`} download={fetchReportFile} hide={() => setPreviewReport(false)} hideTitle={true}/>}
      <div>
        <div className="flex-container">
          {
            SubscriberScopeEnum.UNDEFINED !== scope &&
            subscribers && subscribers[scope] && <>
              <div className="col-md-12">
                <h3 className="h5 u-mbs">{t('validation.customer-info-recall')}</h3>
                {subscribers[scope].isMoralPerson ? <MoralPerson subscriber={subscribers[scope]}/> : <PhysicalPerson subscriber={subscribers[scope]} cosubscriber={subscribers[scope+'_cosous']}/>}
              </div>
            </>
          }
          <div className={`u-mbm u-mts col-md-12`}>{getKycDownloadWidget()}</div>
          {Object.entries(receipts).map(([type, docs], index) => (
            <div key={type} className="col-md-12">
              <h4 className={`${index > 0 ? 'u-mtm' : ''}`}>{type}</h4>
              {docs.map((receipt) => (
                <Receipt key={receipt.id} receipt={receipt} kyc={findResult(receipt.elementId, receipt)} subscriber={subscribers?.[scope]}/>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ReceiptsValidationTab
